// Featured-three homepage pricing — audit D2.3 / D2.2.
//
// Replaces the ten-card homepage carousel with three curated plans
// (Kickstart 3 as the presale pick, Starter as the subscription entry,
// Enterprise as the ERP path) plus a single "Compare all plans on the
// pricing page" link. The full ten-plan comparison still lives on
// /pricing — this component intentionally does NOT duplicate it.

import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Repeat, Building2, Check } from "lucide-react";

const PLANS = [
  {
    key: "k3",
    group: "Lifetime · Presale pick",
    name: "Kickstart 3",
    price: "€199",
    unit: "one-time",
    tagline: "The complete lifetime business suite",
    features: [
      "Lifetime access — no monthly fee",
      "All four AI specialists",
      "Full CRM, projects, finance & marketing",
    ],
    href: "/pricing#kickstart",
    cta: "See Kickstart 3",
    icon: Sparkles,
    accent: "#D4AF37",
    featured: true,
  },
  {
    key: "starter",
    group: "Subscription · Entry",
    name: "Starter",
    price: "€499",
    unit: "/month",
    tagline: "For SME owner-operators getting started",
    features: [
      "One workspace · up to 8 users",
      "AI specialists, CRM, projects, finance",
      "Website Builder with hosting & SSL",
    ],
    href: "/pricing#subscriptions",
    cta: "See Starter",
    icon: Repeat,
    accent: "#202B60",
  },
  {
    key: "enterprise",
    group: "ERP · All 12 modules",
    name: "Enterprise",
    price: "from €2,499",
    unit: "/month",
    tagline: "The full ERP path for growing SMEs",
    features: [
      "All 12 AI-native ERP modules",
      "Multi-workspace, custom seat counts",
      "Founder-assisted migration & onboarding",
    ],
    href: "/pricing#enterprise",
    cta: "Talk to Sales",
    icon: Building2,
    accent: "#D4AF37",
  },
];

export default function PricingFeaturedThree() {
  return (
    <section
      className="zy-section bg-white"
      aria-labelledby="pricing-featured-title"
      data-testid="pricing-featured-three"
      style={{ paddingTop: 80, paddingBottom: 80 }}
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="zy-eyebrow mb-3">Pricing</p>
          <h2 id="pricing-featured-title" className="zy-h2">
            Three ways to start. One connected platform.
          </h2>
          <p className="zy-body mt-4">
            A lifetime deal if you want to own it, a monthly subscription
            if you want to start light, and the full ERP path when you
            need all 12 modules. Everything else — Site Basis, Kickstart
            1 &amp; 2, Creator, Business, Agency — lives on the pricing
            page.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <article
                key={p.key}
                data-testid={`pricing-featured-${p.key}`}
                className={`relative flex flex-col rounded-2xl border p-7 bg-white ${
                  p.featured
                    ? "border-[#D4AF37] shadow-[0_18px_44px_-18px_rgba(10,22,40,0.35)]"
                    : "border-[#eef1f7]"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#0A1628]">
                    Presale pick
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                    style={{ background: `${p.accent}15`, color: p.accent }}
                  >
                    <Icon size={18} />
                  </span>
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#0A1628]/60">
                    {p.group}
                  </p>
                </div>

                <h3 className="mt-5 text-[22px] font-bold text-[#0A1628] leading-tight">
                  {p.name}
                </h3>
                <p className="mt-1 text-[14px] text-[#0A1628]/70">{p.tagline}</p>

                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="text-[30px] font-bold tracking-tight text-[#0A1628]">
                    {p.price}
                  </span>
                  <span className="text-[13px] text-[#0A1628]/60">
                    {p.unit}
                  </span>
                </div>

                <ul className="mt-5 space-y-2.5 text-[13.5px] text-[#0A1628]/80">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check
                        size={15}
                        className="mt-0.5 text-[#202B60] shrink-0"
                        aria-hidden="true"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={p.href}
                  data-testid={`pricing-featured-cta-${p.key}`}
                  className="mt-auto pt-6 inline-flex items-center justify-between gap-2 text-[14px] font-semibold hover:underline"
                  style={{ color: p.accent }}
                >
                  <span>{p.cta}</span>
                  <ArrowRight size={14} />
                </Link>
              </article>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/pricing"
            data-testid="pricing-featured-compare-all"
            className="inline-flex items-center gap-2 rounded-xl border border-[#eef1f7] bg-white px-6 py-3 text-[14px] font-semibold text-[#0A1628] hover:border-[#202B60] hover:text-[#202B60] transition-colors"
          >
            Compare all plans on the pricing page
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
